import { css, cx } from '@linaria/atomic';
import { theme } from '@frontend/theme';

export interface FieldWrapperStyleProps {
  active?: boolean;
  disabled?: boolean;
  hasPadding?: boolean;
  hasError?: boolean;
}

const fieldWrapperBase = css`
  height: 40px;
  border: 1px solid ${theme.colors.neutral30};
  border-radius: ${theme.borderRadius.small};
  color: ${theme.colors.neutral90};
  display: flex;
  align-items: center;
  margin: 0;
  position: relative;
  transition: outline-color 250ms ease-out;
  outline-color: transparent;

  @supports (selector(:has(*))) {
    &:has(:is(input, textarea):focus) {
      box-shadow: 0 0 0 1px var(--field-wrapper-focus-color, ${theme.colors.neutral30});
    }
  }

  @supports not (selector(:has(*))) {
    /**
     * Unfortunately, Firefox does not support :has without a feature flag.
     * This means that Firefox users will see the outline around the input and any other internal elements that have outlines
     */
    &:focus-within {
      box-shadow: 0 0 0 1px var(--field-wrapper-focus-color, ${theme.colors.neutral30});
    }
  }

  input,
  textarea {
    width: 100%;
    background: transparent;
    border: none;
    color: ${theme.colors.neutral90};
    display: block;
    font-family: ${theme.font.family};
    font-size: ${theme.fontSize(16)};
    line-height: 1.375;
    text-align: left;
    -webkit-appearance: none;

    &:focus {
      outline: none;
    }
  }

  input::placeholder,
  textarea::placeholder {
    color: ${theme.colors.neutral50};
  }

  /* anything other than a label before an input? needs left margin */
  > *:not(label) + input {
    margin-left: ${theme.spacing(1)};
  }

  /* if the input isn't the last child, it needs right margin */
  input:not(:last-child) {
    margin-right: ${theme.spacing(1)};
  }
`;

const fieldWrapperBorderDefault = css`
  border-color: ${theme.colors.neutral30};
  --field-wrapper-focus-color: ${theme.colors.neutral30};
`;

const fieldWrapperActive = css`
  border-color: ${theme.colors.primary50};
  --field-wrapper-focus-color: ${theme.colors.primary50};
`;

const fieldWrapperHasError = css`
  border-color: ${theme.colors.status.critical};
  --field-wrapper-focus-color: ${theme.colors.status.critical};

  input::placeholder,
  textarea::placeholder {
    color: ${theme.colors.status.critical};
  }
`;

const fieldWrapperDisabled = css`
  border-color: ${theme.colors.neutral20};
  --field-wrapper-focus-color: ${theme.colors.neutral20};

  input,
  textarea {
    color: ${theme.colors.neutral20};
  }

  input::placeholder,
  textarea::placeholder {
    color: ${theme.colors.neutral20};
  }

  svg {
    color: ${theme.colors.neutral20};
  }
`;

const fieldWrapperHasPadding = css`
  padding: ${theme.spacing(0, 2)};
`;

export const getFieldWrapperClassName = ({ active, disabled, hasError, hasPadding }: FieldWrapperStyleProps) =>
  cx(
    fieldWrapperBase,
    disabled
      ? fieldWrapperDisabled
      : hasError
      ? fieldWrapperHasError
      : active
      ? fieldWrapperActive
      : fieldWrapperBorderDefault,
    hasPadding && fieldWrapperHasPadding
  );

export const FieldWrapperStyles = {
  fieldWrapper: getFieldWrapperClassName,
};
